/**
 * AWS Credential Manager - Manages different AWS credential sets for authentication scenarios
 * Provides secure credential selection based on user authentication state
 */
export class AWSCredentialManagerError extends Error {
    constructor(message, code, originalError) {
        super(message);
        this.code = code;
        this.originalError = originalError;
        this.name = "AWSCredentialManagerError";
    }
}
/**
 * AWS Credential Manager class for handling different credential sets
 * based on user authentication state
 */
export class AWSCredentialManager {
    /**
     * Get authorized user credentials (Distro Nation Audio)
     * These credentials have permissions according to reporting-access-policy.json
     */
    static getAuthCredentials() {
        const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID_AUTH;
        const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY_AUTH;
        if (!accessKeyId || !secretAccessKey) {
            throw new AWSCredentialManagerError("Authorized AWS credentials not found in environment variables. Check AWS_ACCESS_KEY_ID_AUTH and AWS_SECRET_ACCESS_KEY_AUTH.", "AUTH_CREDENTIALS_MISSING");
        }
        return {
            accessKeyId,
            secretAccessKey,
            region: this.AWS_REGION,
        };
    }
    /**
     * Get unauthorized user credentials (Amplify Developer)
     * These credentials should NOT have permissions to the S3 bucket
     */
    static getUnauthCredentials() {
        const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID_UNAUTH;
        const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY_UNAUTH;
        if (!accessKeyId || !secretAccessKey) {
            throw new AWSCredentialManagerError("Unauthorized AWS credentials not found in environment variables. Check AWS_ACCESS_KEY_ID_UNAUTH and AWS_SECRET_ACCESS_KEY_UNAUTH.", "UNAUTH_CREDENTIALS_MISSING");
        }
        return {
            accessKeyId,
            secretAccessKey,
            region: this.AWS_REGION,
        };
    }
    /**
     * Get appropriate credentials based on user authentication state
     * @param isAuthenticated - Whether the user is authenticated via Firebase
     * @returns AWS credentials appropriate for the user's auth state
     */
    static getCredentialsForUser(isAuthenticated) {
        try {
            if (isAuthenticated) {
                return this.getAuthCredentials();
            }
            else {
                return this.getUnauthCredentials();
            }
        }
        catch (error) {
            console.error("Error getting AWS credentials:", error);
            throw new AWSCredentialManagerError(`Failed to get ${isAuthenticated ? "authorized" : "unauthorized"} credentials: ${error instanceof Error ? error.message : "Unknown error"}`, "CREDENTIAL_RETRIEVAL_FAILED", error instanceof Error ? error : undefined);
        }
    }
    /**
     * Validate that all required environment variables are present
     * @returns Validation result with details of any missing variables
     */
    static validateCredentialEnvironment() {
        const errors = [];
        const requiredVars = [
            "AWS_ACCESS_KEY_ID_AUTH",
            "AWS_SECRET_ACCESS_KEY_AUTH",
            "AWS_ACCESS_KEY_ID_UNAUTH",
            "AWS_SECRET_ACCESS_KEY_UNAUTH",
        ];
        for (const varName of requiredVars) {
            if (!process.env[varName]) {
                errors.push(`Missing environment variable: ${varName}`);
            }
        }
        return {
            isValid: errors.length === 0,
            errors,
        };
    }
    /**
     * Validate that specific credentials are properly formatted
     * @param credentials - Credentials to validate
     * @returns Validation result
     */
    static validateCredentials(credentials) {
        const errors = [];
        if (!credentials.accessKeyId) {
            errors.push("Access Key ID is required");
        }
        else if (!credentials.accessKeyId.startsWith("AKIA")) {
            errors.push("Access Key ID format appears invalid (should start with AKIA)");
        }
        if (!credentials.secretAccessKey) {
            errors.push("Secret Access Key is required");
        }
        else if (credentials.secretAccessKey.length < 20) {
            errors.push("Secret Access Key appears too short");
        }
        if (!credentials.region) {
            errors.push("Region is required");
        }
        return {
            isValid: errors.length === 0,
            errors,
        };
    }
    /**
     * Get AWS region configuration
     */
    static getRegion() {
        return this.AWS_REGION;
    }
    /**
     * Test credential access (for debugging purposes)
     * This method can be used to verify credentials are loaded correctly
     */
    static debugCredentials() {
        let authAccessKeyId;
        let unauthAccessKeyId;
        let hasAuthCredentials = false;
        let hasUnauthCredentials = false;
        try {
            const authCreds = this.getAuthCredentials();
            hasAuthCredentials = true;
            authAccessKeyId = authCreds.accessKeyId;
        }
        catch (error) {
            // Credentials not available
        }
        try {
            const unauthCreds = this.getUnauthCredentials();
            hasUnauthCredentials = true;
            unauthAccessKeyId = unauthCreds.accessKeyId;
        }
        catch (error) {
            // Credentials not available
        }
        return {
            auth: {
                hasCredentials: hasAuthCredentials,
                accessKeyId: authAccessKeyId,
            },
            unauth: {
                hasCredentials: hasUnauthCredentials,
                accessKeyId: unauthAccessKeyId,
            },
            environment: this.validateCredentialEnvironment(),
        };
    }
}
AWSCredentialManager.AWS_REGION = "us-east-1";
// Export default instance for convenience
export default AWSCredentialManager;
